import * as yup from 'yup';
import { string, object } from 'yup';
import { CardShareType } from '@/api/general';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc';
dayjs.extend(timezone);
dayjs.extend(utc);
export const MinCharacterLength = (min: number) => {
    return `Must be at least ${min} characters`;
};
export const MaxCharacterLength = (max: number) => {
    return `Must be at most ${max} characters`;
};
const avoidSpacesPattern = new RegExp(/^[^\s]+$/);

export const emailValidationRegexp = new RegExp(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);

export const schemaSearchOrder = yup.object().shape({
    searchBy: yup
        .string()
        .transform((value) => (value === '' ? undefined : value))
        .min(4, MinCharacterLength(4))
        .max(60, MaxCharacterLength(60))
        .notRequired(),
});

// One rule for the order reference wherever it is typed in (order form, quick card)
// The API accepts [a-zA-Z0-9_]* only - the whole value, not just one character of it
const orderReferencePattern = new RegExp(/^[a-zA-Z0-9_]+$/);
const orderReferenceValidation = yup
    .string()
    .required('Required field')
    .matches(avoidSpacesPattern, 'Spaces are not allowed in the Order Reference')
    .matches(orderReferencePattern, 'Only letters, digits and underscore are allowed')
    .min(2, MinCharacterLength(2))
    .max(60, MaxCharacterLength(60));

export const schemaOrderDetails = yup.object().shape({
    reference: orderReferenceValidation,
    customerName: yup
        .string()
        .transform((value) => (value === '' ? undefined : value))
        .min(1, MinCharacterLength(1))
        .max(100, MaxCharacterLength(100)),
    customerReference: yup
        .string()
        .transform((value) => (value === '' ? undefined : value))
        .min(1, MinCharacterLength(1))
        .max(60, MaxCharacterLength(60)),
    description: yup
        .string()
        .transform((value) => (value === '' ? undefined : value))
        .min(1, MinCharacterLength(1))
        .max(255, MaxCharacterLength(255)),
    costCentre: yup
        .string()
        .transform((value) => (value === '' ? undefined : value))
        .min(1, MinCharacterLength(1))
        .max(20, MaxCharacterLength(20)),
});

export const schemaNewCard = yup.object().shape({
    flowType: yup.string().notRequired(), // existing card or new card
    cardId: string().when('flowType', {
        is: 'existingCard',
        then: (schema) => schema.required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    scheme: string().when('flowType', {
        is: 'newCard',
        then: (schema) => schema.required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    productType: string().when('flowType', {
        is: 'newCard',
        then: (schema) => schema.required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    settlementCurrency: string().when('flowType', {
        is: 'newCard',
        then: (schema) => schema.required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    name: yup
        .string()
        .transform((value) => (value === '' ? null : value))
        .min(1, MinCharacterLength(1))
        .max(62, MaxCharacterLength(62))
        .notRequired(),
    cardShareType: yup
        .string()
        .oneOf([CardShareType.Merchant, CardShareType.Individual], 'Required field')
        .default(CardShareType.Merchant)
        .required('Required field'),
    singleUse: yup.boolean().notRequired(),
    // fundingMethod: yup.string().notRequired(),
    // spendLimit: yup.number().notRequired(),
    // usage: yup.string().notRequired(),
    // spendTolerance: yup.number().notRequired(),
    // mccAllowList: yup.array().notRequired(),
    // mccDenyList: yup.array().notRequired(),
    // countryAllowList: yup.array().notRequired(),
    // countryDenyList: yup.array().notRequired(),
});

export const createCardSchema = yup.object({
    name: yup.string().max(62).notRequired(),
    scheme: yup.string().notRequired(),
    singleUse: yup.boolean(),
    // Always present: the form defaults to a merchant share and only offers a choice where
    // Individual is available. The default also covers cards issued before the field existed,
    // which the edit form loads without it.
    cardShareType: yup
        .string()
        .oneOf([CardShareType.Merchant, CardShareType.Individual], 'Required field')
        .default(CardShareType.Merchant)
        .required('Required field'),
    productType: yup.string().required('Required field'),
    settlementCurrency: yup
        .string()
        .default(undefined)
        .required('Required field')
        .matches(/^[A-Z]{3}$/),
});

export const apiKeySchema = yup.object().shape({
    description: yup
        .string()
        .required('Required field')
        .max(400, 'Max length of description is 400'),
});

export const linkPaymentSchema = yup.object().shape({
    reference: yup.string().required('Required field').nullable(),
    paymentInstruction: yup.string().required('Required field').nullable(),
});

export const webhookSchema = yup.object().shape({
    uri: yup
        .string()
        .required('Required field')
        .matches(/^https:\/\/[\S]+$/, 'Wrong format'),
    name: yup
        .string()
        .required('Required field')
        .matches(/^[a-zA-Z!@#$%^&*()\[\]{}\\+-|;:_'",<.>/?`~]*$/, 'Wrong format')
        .min(1, MinCharacterLength(1))
        .max(64, MaxCharacterLength(64)),
    value: yup
        .string()
        .required('Required field')
        .matches(/^[^\s]*$/, 'Wrong format')
        .min(1, MinCharacterLength(1))
        .max(128, MaxCharacterLength(128)),
});

export const inboundPaymentSchema = yup.object().shape({
    currency: yup.string().required('Required field'),
    amount: yup
        .number()
        .typeError('Must be a number')
        .min(0.01, 'Must be at least 0.01')
        .required('Required field')
        .nullable()
        .notOneOf([null]),
    paymentMethod: yup.string().required('Required field'),
    transactionId: yup
        .string()
        .transform((value) => (value === '' ? undefined : value))
        .min(1, MinCharacterLength(1))
        .max(60, MaxCharacterLength(60))
        .notRequired(),
    acquirerReferenceNumber: yup
        .string()
        .transform((value) => (value === '' ? undefined : value))
        .min(1, MinCharacterLength(1))
        .max(60, MaxCharacterLength(60))
        .notRequired(),
    date: yup.string().nullable(),
});

export const suspendCardSchema = yup.object().shape({
    reasonType: yup.string().required(),
    comment: yup
        .string()
        .required('Required field')
        .min(1, MinCharacterLength(1))
        .max(128, MaxCharacterLength(128)),
});

export const accountDetailsSchema = yup.object().shape({
    email: yup
        .string()
        .transform((value) => (value === '' ? undefined : value))
        .matches(emailValidationRegexp, 'Does not match the standard format for emails')
        .required('Required field'),
    name: yup
        .string()
        .required('Required field')
        .min(1, MinCharacterLength(1))
        .max(255, MaxCharacterLength(255)),
});

// Schemes whose creditor address must carry a postal code; the ZIP input reads the same list
// for its required mark, so the label and the validation can never disagree.
export const ZIP_CODE_REQUIRED_SCHEMES = ['FAST', 'SWIFT', 'SEPA', 'INST'];

export const ibanCreditorInfoSchema = yup.object().shape({
    paymentScheme: yup.string().required('Required field'),
    creditorName: yup
        .string()
        .min(2, MinCharacterLength(2))
        .max(64, MaxCharacterLength(64))
        .required('Required field'),
    creditorReference: yup
        .string()
        .required('Required field')
        // Charset and length are validated separately so each failure shows its
        // own message ("Must be valid reference" vs the 18-character limit).
        .matches(/^[a-zA-Z0-9]*$/g, 'Must be valid reference')
        .max(18, MaxCharacterLength(18)),

    secondaryReference: yup.string().max(140, MaxCharacterLength(140)),
    ownerType: yup.string().max(140, MaxCharacterLength(140)),
    accountNumber: yup.string().when('paymentScheme', {
        is: (val: string) => val === 'FAST',
        then: (schema) =>
            schema.matches(/^[0-9]{8}$/g, 'Must be valid number').required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    sortCode: yup.string().when('paymentScheme', {
        is: (val: string) => val === 'FAST',
        then: (schema) =>
            schema.matches(/^[0-9]{6}$/g, 'Must be valid sort code').required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    iban: yup.string().when('paymentScheme', {
        is: (val: string) => ['SWIFT', 'SEPA', 'INST'].includes(val),
        then: (schema) =>
            schema
                .matches(
                    /^([A-Z]{2}\d{2}[A-Z0-9]{12,})|([A-Z]{4}[A-Z2-9]{24})$/g,
                    'Must be valid IBAN',
                )
                .required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    bankName: yup.string().when('paymentScheme', {
        is: (val: string) => ['SWIFT', 'SEPA', 'INST'].includes(val),
        then: (schema) =>
            schema
                .min(2, MinCharacterLength(2))
                .max(35, MaxCharacterLength(35))
                .required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    bicCode: yup.string().when('paymentScheme', {
        is: (val: string) => ['SWIFT', 'SEPA', 'INST'].includes(val),
        then: (schema) =>
            schema
                .matches(/^[A-Z]{6,6}[A-Z0-9]{2,5}$/g, 'Must be valid code')
                .required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    addressLine1: yup
        .string()
        .required('Required field')
        .min(0, MinCharacterLength(0))
        .max(35, MaxCharacterLength(35)),
    city: yup
        .string()
        .required('Required field')
        .min(0, MinCharacterLength(0))
        // SWIFT scheme limit for a structured address town name
        .max(35, MaxCharacterLength(35)),
    country: yup.string().required('Required field'),
    zipCode: yup.string().when('paymentScheme', {
        is: (val: string) => ZIP_CODE_REQUIRED_SCHEMES.includes(val),
        then: (schema) =>
            schema
                .required('Required field')
                .min(1, MinCharacterLength(1))
                .max(10, MaxCharacterLength(10)),
        otherwise: (schema) =>
            schema.notRequired().min(1, MinCharacterLength(1)).max(10, MaxCharacterLength(10)),
    }),
});

export const ibanPaymentInfoSchema = yup.object().shape({
    currency: yup.string().required('Required field'),
    supplierName: yup.string().max(60, MaxCharacterLength(60)).notRequired(),
    bankCountry: yup.string().required('Required field'),
    description: yup
        .string()
        .min(2, MinCharacterLength(2))
        .max(64, MaxCharacterLength(64))
        .required('Required field'),
    activeFrom: yup.string().required('Required field'),
    fundingMethod: yup.string().required('Required field'),
    amount: yup
        .number()
        .typeError('Must be a number')
        .min(0.01, 'Must be at least 0.01')
        .max(999999999999999, 'Must be less then 999999999999999')
        .required('Required field'),
    paymentTimeTimeZone: yup.string().when('activeFrom', {
        is: (val: string) => val === 'schedule',
        then: (schema) => schema.required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    paymentTime: string().when('activeFrom', {
        is: (val: string) => val === 'schedule',
        then: (schema) => schema.required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    paymentDate: yup.string().when('activeFrom', {
        is: (val: string) => val === 'schedule',
        then: (schema) => schema.required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
});

export const simulateTransactionSchema = yup.object().shape({
    type: yup.string().required('Required field'),
    acquirerAmount: yup
        .number()
        .typeError('Must be a number')
        .min(0, 'Must be at least 0.01')
        .required('Required field')
        .nullable()
        .notOneOf([null]),
    acquirerCurrency: yup.string().required('Required field'),
    billingAmount: yup
        .number()
        .typeError('Must be a number')
        .min(0, 'Must be at least 0.01')
        .required('Required field')
        .nullable()
        .notOneOf([null]),
    billingCurrency: yup.string().required('Required field'),
    countryCode: yup.string().required('Required field'),
    mcc: yup.string().required('Required field'),
    transactionId: yup.string().notRequired(),
});

export const quickCardSchema = object({
    orderReference: orderReferenceValidation,
    productType: yup.string().required('Required field'),
    currency: yup.string().required('Required field'),
    amount: yup
        .number()
        .typeError('Must be a number')
        .min(0.01, 'Must be at least 0.01')
        .required('Required field'),
    authTolerance: yup
        .number()
        .transform((value, originalValue) => {
            if (originalValue === '') return null;
            return value;
        })
        .nullable()
        .typeError('Must be a number')
        .min(0, 'Must be at least 0')
        .max(1000, 'Must be less than 1000')
        .notRequired(),
    supplierName: yup.string().max(60, MaxCharacterLength(60)).notRequired(),
    mccAllowList: yup.array().notRequired(),
    mccDenyList: yup.array().notRequired(),
    countryAllowList: yup.array().notRequired(),
    countryDenyList: yup.array().notRequired(),
    fundingMethod: yup.string().required('Required field'),
    scheme: yup.string().required(),
});

export const createUserSchema = object({
    name: yup
        .string()
        .min(1, 'Minimum allowed length is 1 character')
        .max(255, 'Maximum allowed length is 255 characters')
        .required('Required field'),
    email: yup
        .string()
        .transform((value) => (value === '' ? undefined : value))
        .matches(emailValidationRegexp, 'Does not match the standard format for emails')
        .required('Required field'),
    roles: yup.array().min(1).required(),
});

export const paymentInfoSchema = object({
    amount: yup
        .number()
        .typeError('Must be a number')
        .min(0.01, 'Must be no less than 0.01')
        .max(999999999999999, 'Must be lees than 999999999999999')
        .required('Required field'),
    authTolerance: yup
        .number()
        .transform((value, originalValue) => {
            if (originalValue === '') return null;
            return value;
        })
        .integer('Value must be an integer')
        .nullable()
        .typeError('Must be a number')
        .min(0, 'Must be at least 0')
        .max(1000, 'Must be less than 1000')
        .notRequired(),
    supplierName: string()
        .transform((value) => (value === '' ? undefined : value))
        .min(1, 'Minimum allowed length is 1 character')
        .max(60, 'Maximum allowed length is 60 characters')
        .notRequired(),
    fundingMethod: yup.string().required('Required field'),
    activeToTimeZone: yup.string().required('Required field'),
    activeToTime: yup.string().required('Required field'),
    activeToDate: yup.string().required('Required field'),
    activeToDateTime: yup
        .object()
        .required('Required field')
        .test('futureTime', 'Must be at least 15 minutes from now', function (value) {
            const { activeFrom, isSingleUse } = this.parent;
            if (activeFrom !== 'schedule' || isSingleUse) {
                return true;
            }
            const currentTime = dayjs();
            // @ts-ignore
            const selectedTime = dayjs.tz(value, this.parent.activeToTimeZone);
            const futureTime = currentTime.add(14, 'minutes');

            return selectedTime.isAfter(futureTime);
        })
        .test(
            'featureTimeAfterActiveFrom',
            'Must be at least 15 minutes from "active from"',
            function (value) {
                const { activeFromDateTime, activeFrom, isSingleUse } = this.parent;
                if (activeFrom !== 'schedule' || isSingleUse) {
                    return true;
                }

                const minimumActiveToDateTime = activeFromDateTime.add(14, 'minutes');

                // @ts-ignore
                return value.isAfter(minimumActiveToDateTime);
            },
        ),
    activeFromTimeZone: yup.string().when('activateForm', {
        is: (val: string) => val === 'schedule',
        then: (schema) => schema.required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    activeFromTime: string().when('activateForm', {
        is: (val: string) => val === 'schedule',
        then: (schema) => schema.required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    activeFromDate: yup.string().when('activateForm', {
        is: (val: string) => val === 'schedule',
        then: (schema) => schema.required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
    orderReference: yup.string().when('orderExist', {
        is: (val: boolean) => !val,
        then: (schema) => schema.required('Required field'),
        otherwise: (schema) => schema.notRequired(),
    }),
});

export const AmountRangeFilterSchema = yup.object().shape({
    min: yup
        .number()
        .typeError('Must be a number')
        .transform((value, originalValue) => {
            if (originalValue === '') return null;
            return value;
        })
        .test('not more than max', 'Must be less or equal Max value', function (value) {
            const max = this.parent.max;
            if (max && value) {
                return max >= value;
            } else {
                return true;
            }
        })
        .notRequired()
        .min(0, 'Should be more than 0'),
    max: yup
        .number()
        .typeError('Must be a number')
        .transform((value, originalValue) => {
            if (originalValue === '') return null;
            return value;
        })
        .test('not less than min', 'Must be more or equal Min value', function (value) {
            const min = this.parent.min;
            if (min && (value || typeof value === 'number')) {
                return value >= min;
            } else {
                return true;
            }
        })
        .notRequired()
        .min(0, 'Should be more than 0'),
});
